import { useEffect, useRef, useState } from "react";

const reducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A gentle scroll parallax. Attach the returned `ref` to an element and offset
 * it by `offset` px (via a translate) so it drifts slower than the page. The
 * offset is derived from the element's distance to the viewport centre, clamped
 * so the effect stays subtle, and is disabled when the visitor prefers reduced
 * motion or `disabled` is passed (e.g. on small screens where the layout stacks).
 */
export function useParallax(speed = 0.06, disabled = false) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (disabled || reducedMotion) {
      setOffset(0);
      return;
    }

    let frame = 0;

    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const elementCentre = rect.top + rect.height / 2;
      const viewportCentre = window.innerHeight / 2;
      const raw = (elementCentre - viewportCentre) * -speed;
      setOffset(Math.max(-60, Math.min(60, raw)));
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [speed, disabled]);

  return { ref, offset };
}
